import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { UserResponse, UpdateClientRequest, Gender } from '../types/api';
import { useTranslation } from '../context/LanguageContext';
import { useConfirmation } from '../hooks/useConfirmation';
import ConfirmationDialog from './ConfirmationDialog';
import './Modal.css';

interface EditClientInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientUpdated: () => void;
  client: UserResponse | null;
}

const EditClientInfoModal: React.FC<EditClientInfoModalProps> = ({ 
  isOpen, 
  onClose, 
  onClientUpdated, 
  client 
}) => {
  const { t } = useTranslation();
  const { confirmation, showConfirmation, hideConfirmation, setLoading, setSuccess: setConfirmationSuccess } = useConfirmation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Client info form
  const [clientInfo, setClientInfo] = useState<UpdateClientRequest>({
    name: '',
    email: '',
    phone: '',
    isActive: true
  });



  useEffect(() => {
    if (isOpen && client) {
      // Set client info from passed data
      setClientInfo({
        name: client.name,
        email: client.email,
        phone: client.phoneNumber || client.phone || '',
        isActive: client.isActive,
        adminNotificationEnabled: client.adminNotificationEnabled || false,
        isMorningClient: client.isMorningClient || false,
        isAfternoonClient: client.isAfternoonClient || false,
        cardiologyProof: client.cardiologyProof || false,
        gender: client.gender ?? null
      });
      

      
      // Clear previous messages
      setError('');
      setSuccess('');
    }
  }, [isOpen, client]);

  const handleUpdateClientInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Update client info with all fields including admin notification and morning client settings
      const updateData: UpdateClientRequest = {
        ...clientInfo
      };

      const response = await apiService.updateClient(client.id, updateData);
      if (response.success) {
        setSuccess(t('clientInfoUpdatedSuccessfully'));
        onClientUpdated();
        // Close modal after short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.message || t('failedToUpdateClient'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorUpdatingClient'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClient = () => {
    if (!client) return;

    showConfirmation({
      title: t('deleteClient'),
      message: `${t('deleteClientConfirmation')} "${client.name}"? ${t('actionCannotBeUndone')}`,
      confirmText: t('delete'),
      cancelText: t('cancel'),
      type: 'danger',
      onConfirm: async () => {
        setLoading(true);
        try {
          const response = await apiService.deleteClient(client.id);
          if (response.success) {
            setConfirmationSuccess(t('clientDeletedSuccessfully'));
            onClientUpdated();
            // Close modal after confirmation dialog closes
            setTimeout(() => {
              onClose();
            }, 2200);
          } else {
            setError(response.message || t('failedToDeleteClient'));
            hideConfirmation();
          }
        } catch (err: any) {
          setError(err.response?.data?.message || t('errorDeletingClient'));
          hideConfirmation();
        }
      },
      onCancel: () => {
        hideConfirmation();
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('editClientInfo')} - {client?.name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="modal-body">
          <form onSubmit={handleUpdateClientInfo} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">{t('fullName')}</label>
            <input
              type="text"
              id="name"
              value={clientInfo.name}
              onChange={(e) => setClientInfo({...clientInfo, name: e.target.value})}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              value={clientInfo.email}
              onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">{t('phone')}</label>
            <input
              type="tel"
              id="phone"
              value={clientInfo.phone}
              onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={clientInfo.isActive}
                onChange={(e) => setClientInfo({...clientInfo, isActive: e.target.checked})}
                disabled={isLoading}
              />
              <span className="checkbox-text">{t('activeAccount')}</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={clientInfo.adminNotificationEnabled || false}
                onChange={(e) => setClientInfo({...clientInfo, adminNotificationEnabled: e.target.checked})}
                disabled={isLoading}
              />
              <span className="checkbox-text">{t('receiveMailNotifications')}</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={clientInfo.isMorningClient || false}
                onChange={(e) => setClientInfo({...clientInfo, isMorningClient: e.target.checked, isAfternoonClient: e.target.checked ? false : clientInfo.isAfternoonClient})}
                disabled={isLoading || clientInfo.isAfternoonClient}
              />
              <span className="checkbox-text">{t('morningClient')}</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={clientInfo.isAfternoonClient || false}
                onChange={(e) => setClientInfo({...clientInfo, isAfternoonClient: e.target.checked, isMorningClient: e.target.checked ? false : clientInfo.isMorningClient})}
                disabled={isLoading || clientInfo.isMorningClient}
              />
              <span className="checkbox-text">{t('afternoonClient')}</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={clientInfo.cardiologyProof || false}
                onChange={(e) => setClientInfo({...clientInfo, cardiologyProof: e.target.checked})}
                disabled={isLoading}
              />
              <span className="checkbox-text">{t('cardiologyProof')}</span>
            </label>
          </div>

          <div className="form-group">
            <label className="gender-label">{t('gender')}</label>
            <div className="gender-toggle">
              <span className={clientInfo.gender === Gender.Male ? 'gender-toggle-text active' : 'gender-toggle-text'}>
                {t('male')}
              </span>
              <label className={`gender-switch${clientInfo.gender == null ? ' unset' : ''}`}>
                <input
                  type="checkbox"
                  checked={clientInfo.gender === Gender.Female}
                  onChange={(e) => setClientInfo({...clientInfo, gender: e.target.checked ? Gender.Female : Gender.Male})}
                  disabled={isLoading}
                />
                <span className="gender-slider"></span>
              </label>
              <span className={clientInfo.gender === Gender.Female ? 'gender-toggle-text active' : 'gender-toggle-text'}>
                {t('female')}
              </span>
            </div>
            {clientInfo.gender == null && (
              <p className="gender-unset-hint">{t('genderNotSet')}</p>
            )}
          </div>

          <div className="modal-actions" style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
            <button
              type="button"
              onClick={handleDeleteClient}
              className="btn-danger"
              disabled={isLoading}
              title={t('deleteClient')}
            >
              {t('delete')}
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                {t('cancel')}
              </button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {t('updateClientInfo')}
              </button>
            </div>
          </div>
          </form>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
        isLoading={confirmation.isLoading}
        isSuccess={confirmation.isSuccess}
        successMessage={confirmation.successMessage}
      />
    </div>
  );
};

export default EditClientInfoModal; 